import { Enums, geometryLoader } from "@cornerstonejs/core";
// @ts-ignore - Type definitions are incomplete
import { PublicContourSetData } from "@cornerstonejs/core/types";
import { segmentation } from "@cornerstonejs/tools";
import { Enums as csToolsEnums } from "@cornerstonejs/tools";
import axios from "axios";
import { computeLabelmapData } from '@cornerstonejs/polymorphic-segmentation';
import {LabelmapSegmentationDataVolume} from "@cornerstonejs/tools/dist/esm/types/LabelmapTypes";
export interface Organ {
  organName: string;
  roiColor: number[];
  slices: Record<string, any>;
}

export interface OrganSegmentation {
  segmentationId: string; // Unique ID for the segmentation
  geometryId: string;     // ID for the geometry
  color: number[];        // The organ's ROI color
  owner: string;          // Owner of the segmentation (e.g., "GS" or student name)
  organName: string;      // Name of the organ
  visible: boolean;       // Whether the segmentation is visible
}

/**
 * Loads and initializes segmentation data for all organs from the server
 * @param caseId The case ID to load segmentations for
 * @param containerSegmentationId Optional parent segmentation ID for reference
 * @returns Array of organ segmentation objects that were loaded
 */
export async function loadAllOrganSegmentations(
  caseId?: string | number,
  containerSegmentationId?: string
): Promise<OrganSegmentation[]> {
  const organSegmentations = await createAndCacheOrganGeometries(caseId);
  
  // Add each organ as a separate segmentation with its own ID
    segmentation.addSegmentations(organSegmentations.map(organSeg => ({
      segmentationId: organSeg.segmentationId,
      representation: {
        type: csToolsEnums.SegmentationRepresentations.Contour,
        data: {
          geometryIds: [organSeg.geometryId],
        },
      },
    })));

  return organSegmentations;
}

/**
 * Processes organ contours and creates geometries for a single owner (GS or student)
 * @param organs Array of organs with contour data
 * @param owner Owner of the organs (GS or student name)
 * @param organSegmentations Array to add the segmentations to
 */
async function processOrganContours(
    organs: Organ[],
    owner: string,
    organSegmentations: OrganSegmentation[]
): Promise<void> {
  const organPromises = organs.map(async (organ) => {
    const organName = organ.organName;

    const geometryId = `${owner}_${organName}`;
    const segmentationId = geometryId;

    const dataArray = [];

    // Process all slices and contours
    Object.entries(organ.slices).forEach(([sliceZ, sliceList]) => {
      for (const sliceId in sliceList as Array<any>) {
        const len = sliceList[sliceId].xCords.length;
        const points = [];

        for (let i = 0; i < len; i++) {
          const xCords = sliceList[sliceId].xCords[i];
          const yCords = sliceList[sliceId].yCords[i];

          if (xCords === undefined || yCords === undefined) {
            console.error(`Undefined coordinates at index ${i} for ${owner}/${organName}`);
          }

          points.push([xCords, yCords, parseInt(sliceZ)]);
        }

        dataArray.push({
          type: "CLOSED_PLANAR",
          points: points,
        });
      }
    });

    // Determine color

    let organColor = organ.roiColor || [0, 222, 0];
    if (owner === "GS") {
      organColor = [255, 0, 0];
    }
    if (dataArray.length === 0) {
      console.warn(`Skipping geometry for ${geometryId}: no contour data`);
      return;
    }

    const geometryData = {
      color: organColor,
      data: dataArray,
      id: geometryId,
    };

    console.log(`Creating geometry for ${geometryId} with color ${organColor}`);

    // Await geometry creation and caching
    await geometryLoader.createAndCacheGeometry(geometryId, {
      type: Enums.GeometryType.CONTOUR,
      geometryData: geometryData as any,
    });



    // Collect segmentation info
    organSegmentations.push({
      segmentationId,
      geometryId,
      color: organColor,
      owner,
      organName,
      visible: owner === "GS"
    });
  });

// Wait for all organs to be processed and geometries created
  await Promise.all(organPromises);
}

/**
 * Creates and caches geometries from contours data fetched from the server
 * @param caseId The case ID to load geometries for
 * @returns Array of organ segmentation objects with their properties
 */
async function createAndCacheOrganGeometries(caseId?: string | number): Promise<OrganSegmentation[]> {
  const currentCaseId = caseId;
  
  try {
    const organSegmentations: OrganSegmentation[] = [];
    
    // 1. First load golden standard contours
    const gsResponse = await axios.get(`/api/file/gs/${currentCaseId}`);
    const gsData = gsResponse.data;
    
    // Process golden standard contours
    await processOrganContours(gsData.organs, "GS", organSegmentations);
    
    // 2. Then load student contours
    try {
      const studentResponse = await axios.get(`/api/file/student/${currentCaseId}`);
      const studentData = studentResponse.data;

      // Response shape: { contours: { [studentName]: ContourStackWeb }, skipped: SkippedFile[] }
      // (Tolerate the legacy flat-map shape too in case an older backend is still deployed.)
      const contours = studentData && studentData.contours
        ? studentData.contours
        : studentData;
      const skipped: Array<{ filename: string; studentNumber: string; displayName: string; reason: string }> =
        (studentData && Array.isArray(studentData.skipped)) ? studentData.skipped : [];

      Object.entries(contours).forEach(([studentName, studentOrgans])=> {
        const studentOrgansObject = studentOrgans as any;
        processOrganContours(studentOrgansObject.organs as Organ[], studentName, organSegmentations);
      });

      if (skipped.length > 0) {
        const lines = skipped.map(s =>
          `• ${s.displayName || s.filename} (${s.reason})`
        );
        console.warn("Skipped student submissions:", skipped);
        alert(
          `${skipped.length} student submission(s) could not be loaded and were skipped:\n\n` +
          lines.join("\n") +
          `\n\nAsk the student(s) to re-upload a valid DICOM file.`
        );
      }
    } catch (studentError) {
      console.warn("Could not load student contours:", studentError);
      // Continue with just the golden standard contours
    }

    return organSegmentations;
  } catch (error) {
    console.error("Error loading segmentation data:", error);
    return [];
  }
}
